'use client';

import { useState, useEffect, useMemo } from 'react';
import { carriedBooksInOrder, type BookId } from '@sharpr/types';
import { useSport } from '@/contexts/SportContext';
import { useBookCatalog } from '@/hooks/useBookCatalog';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { resolveVisibleSportsbooks } from '@/utils/sportsbookPreferences';

export function useOrderedSportsbooks() {
  const { preferences, updateOrderedSportsbooks } = useUserPreferences();
  const catalog = useBookCatalog();
  const sport = useSport();
  // A column for a book this sport's feed never carries would stay empty.
  const available = useMemo(() => carriedBooksInOrder(catalog, sport).map((b) => b.id), [catalog, sport]);

  const resolved = resolveVisibleSportsbooks(
    available,
    preferences?.ordered_sportsbooks ?? [],
    preferences?.hidden_sportsbooks ?? [],
  );

  const [sportsbooksOrder, setSportsbooksOrder] = useState<BookId[]>(resolved);

  useEffect(() => {
    setSportsbooksOrder(
      resolveVisibleSportsbooks(
        available,
        preferences?.ordered_sportsbooks ?? [],
        preferences?.hidden_sportsbooks ?? [],
      ),
    );
  }, [available, preferences?.ordered_sportsbooks, preferences?.hidden_sportsbooks]);

  const handleReorderSportsbooks = async (newOrder: BookId[]) => {
    // Without the catalog nothing here can be told from an unknown book: send no write.
    if (catalog.size === 0) return;
    setSportsbooksOrder(newOrder);
    // The stored order is shared by both sports: the reordered columns take the places they held, and every book not
    // shown here (hidden, or not carried by this sport's feed) keeps its own.
    const shown = new Set(newOrder);
    const stored = resolveVisibleSportsbooks(carriedBooksInOrder(catalog).map((b) => b.id), preferences?.ordered_sportsbooks ?? [], []);
    let next = 0;
    const merged = stored.map((b) => (shown.has(b) ? newOrder[next++] : b));
    try {
      await updateOrderedSportsbooks([...merged, ...newOrder.slice(next)]);
    } catch (error) {
      console.error('Failed to save sportsbook order:', error);
      setSportsbooksOrder(resolved);
    }
  };

  return { sportsbooksOrder, handleReorderSportsbooks };
}
